import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';
import { io } from 'socket.io-client';

const API = 'https://blitzly.tv/api';
const WS_URL = 'https://blitzly.tv';

const colors = {
  bg: '#0a0a0a',
  surface: '#111111',
  surfaceAlt: '#1a1a1a',
  border: '#222222',
  accent: '#00c896',
  accentDim: '#00c89620',
  text: '#f0f0f0',
  textMuted: '#666666',
  textSub: '#999999',
  danger: '#e05555',
};

const s = {
  splashPage: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#0a0a0a' },
  splashLogo: { width: 'min(420px, 60vmin)', height: 'min(420px, 60vmin)', objectFit: 'contain', opacity: 0, transform: 'scale(0.94)', animation: 'splashIn 0.9s cubic-bezier(0.16,1,0.3,1) forwards' },
  page: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: colors.bg, fontFamily: "'Sora', sans-serif" },
  card: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '40px 36px', width: '340px' },
  logoRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' },
  logoIcon: { fontSize: '28px', lineHeight: 1 },
  logoText: { fontSize: '24px', fontWeight: '700', color: colors.text, letterSpacing: '-0.5px' },
  tagline: { color: colors.textMuted, fontSize: '14px', marginBottom: '28px' },
  label: { display: 'block', fontSize: '12px', color: colors.textSub, marginBottom: '6px', letterSpacing: '0.5px', textTransform: 'uppercase' },
  input: { width: '100%', padding: '10px 12px', background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: '8px', color: colors.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '16px' },
  btn: { width: '100%', padding: '11px', background: colors.accent, color: '#000', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginTop: '4px', letterSpacing: '0.2px' },
  btnGhost: { width: '100%', padding: '11px', background: 'transparent', color: colors.textSub, border: `1px solid ${colors.border}`, borderRadius: '8px', fontSize: '14px', cursor: 'pointer', marginTop: '8px' },
  error: { color: colors.danger, fontSize: '13px', marginBottom: '12px', padding: '8px 12px', background: '#e0555510', borderRadius: '6px' },
  success: { color: colors.accent, fontSize: '13px', marginBottom: '12px', padding: '8px 12px', background: colors.accentDim, borderRadius: '6px' },
  divider: { display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0', color: colors.textMuted, fontSize: '12px' },
  dividerLine: { flex: 1, height: '1px', background: colors.border },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', borderBottom: `1px solid ${colors.border}`, background: colors.surface, position: 'sticky', top: 0, zIndex: 10 },
  headerLogo: { display: 'flex', alignItems: 'center', gap: '8px' },
  headerLogoText: { fontSize: '18px', fontWeight: '700', color: colors.text, letterSpacing: '-0.3px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  eloTag: { background: colors.accentDim, color: colors.accent, fontSize: '12px', fontWeight: '600', padding: '4px 10px', borderRadius: '20px' },
  btnTiny: { padding: '6px 12px', background: 'transparent', color: colors.textMuted, border: `1px solid ${colors.border}`, borderRadius: '6px', fontSize: '13px', cursor: 'pointer' },
  gameLayout: { display: 'flex', gap: '28px', padding: '28px 24px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'flex-start' },
  gameLayoutMobile: { display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px', alignItems: 'center' },
  boardShell: { width: '420px', maxWidth: '420px', flexShrink: 0 },
  panel: { width: '220px', paddingTop: '4px' },
  panelMobile: { width: '100%', maxWidth: '420px', paddingTop: '0' },
  panelSection: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '10px', padding: '16px', marginBottom: '12px' },
  panelLabel: { fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' },
  statusText: { fontSize: '16px', fontWeight: '600', color: colors.accent, lineHeight: 1.4 },
  resetBtn: { width: '100%', padding: '10px', background: 'transparent', color: colors.textSub, border: `1px solid ${colors.border}`, borderRadius: '8px', fontSize: '13px', cursor: 'pointer', marginTop: '4px' },
  hint: { fontSize: '12px', color: colors.textMuted, lineHeight: 1.6 },
  moveList: { fontSize: '13px', color: colors.textSub, lineHeight: 1.8, fontFamily: 'monospace' },
  diffOption: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '12px 14px', background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', textAlign: 'left' },
  diffOptionActive: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '12px 14px', background: colors.accentDim, border: `1px solid ${colors.accent}`, borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', textAlign: 'left' },
  diffName: { fontSize: '14px', fontWeight: '600', color: colors.text },
  diffElo: { fontSize: '12px', color: colors.textMuted },
  colorPickRow: { display: 'flex', gap: '8px' },
  colorPickBtn: { flex: 1, padding: '12px', borderRadius: '8px', fontSize: '26px', lineHeight: 1, cursor: 'pointer', textAlign: 'center' },
  colorPickBtnWhite: { background: '#f2f2f0', color: '#101010', border: `1px solid ${colors.border}` },
  colorPickBtnBlack: { background: '#000000', color: '#f2f2f0', border: `1px solid ${colors.border}` },
};

const DIFFICULTIES = [
  { name: 'Beginner', elo: 400 },
  { name: 'Easy', elo: 800 },
  { name: 'Casual', elo: 1200 },
  { name: 'Intermediate', elo: 1500 },
  { name: 'Advanced', elo: 1800 },
  { name: 'Expert', elo: 2200 },
  { name: 'Master', elo: 2600 },
];

// ---------------------------------------------------------------------------
// Shared hooks
// ---------------------------------------------------------------------------

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

// ---------------------------------------------------------------------------
// Splash
// ---------------------------------------------------------------------------

function Splash({ onDone }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 2200);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <div style={s.splashPage}>
      <img src="/logo.jpg" alt="Blitzly" style={s.splashLogo} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

function Login({ onLogin, redirectTo }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('login');
  const [msg, setMsg] = useState(null);
  const navigate = useNavigate();

  const submit = async () => {
    setMsg(null);
    if (!username.trim() || !password.trim()) return setMsg({ type: 'error', text: 'Please fill in all fields.' });
    if (username.trim().length < 3) return setMsg({ type: 'error', text: 'Username must be at least 3 characters.' });
    if (mode === 'register' && password.length < 6) return setMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
    try {
      const res = await fetch(`${API}/${mode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json();
      if (data.error) return setMsg({ type: 'error', text: data.error });
      if (mode === 'register') { setMode('login'); setMsg({ type: 'success', text: 'Account created. Please log in.' }); return; }
      onLogin(data);
      if (redirectTo) navigate(redirectTo, { replace: true });
    } catch {
      setMsg({ type: 'error', text: 'Could not connect to server.' });
    }
  };

  const onKey = (e) => { if (e.key === 'Enter') submit(); };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logoRow}><span style={s.logoIcon}>♟</span><span style={s.logoText}>Blitzly</span></div>
        <p style={s.tagline}>Chess video chat</p>
        {msg && <p style={msg.type === 'error' ? s.error : s.success}>{msg.text}</p>}
        <label style={s.label}>Username</label>
        <input style={s.input} value={username} onChange={e => setUsername(e.target.value)} onKeyDown={onKey} autoFocus />
        <label style={s.label}>Password</label>
        <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={onKey} />
        <button style={s.btn} onClick={submit}>{mode === 'login' ? 'Log in' : 'Create account'}</button>
        <div style={s.divider}><div style={s.dividerLine} /><span>or</span><div style={s.dividerLine} /></div>
        <button style={s.btnGhost} onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setMsg(null); }}>
          {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Log in'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mode picker (new screen between Login and DifficultyPicker)
// ---------------------------------------------------------------------------

function ModePicker({ user, onSelectBot, onLogout }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState(null);

  const createRoom = async () => {
    setCreating(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
      });
      const data = await res.json();
      if (data.error) { setErr(data.error); return; }
      navigate(`/room/${data.id}`);
    } catch {
      setErr('Could not connect to server.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logoRow}><span style={s.logoIcon}>♟</span><span style={s.logoText}>Blitzly</span></div>
        <p style={s.tagline}>Welcome, {user.username}</p>
        {err && <p style={s.error}>{err}</p>}
        <button style={s.btn} onClick={() => navigate('/play')}>Play</button>
        <button style={{ ...s.btn, marginTop: '10px', background: colors.surfaceAlt, color: colors.text, border: `1px solid ${colors.border}` }} onClick={onSelectBot}>Play vs Bot</button>
        <button style={{ ...s.btn, marginTop: '10px', background: colors.surfaceAlt, color: colors.text, border: `1px solid ${colors.border}` }} onClick={createRoom} disabled={creating}>
          {creating ? 'Creating room...' : 'Create Room'}
        </button>
        <button style={s.btnGhost} onClick={onLogout}>Log out</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Difficulty picker
// ---------------------------------------------------------------------------

function DifficultyPicker({ onSelect }) {
  const [selected, setSelected] = useState(DIFFICULTIES[2]);
  const [color, setColor] = useState('white');

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logoRow}><span style={s.logoIcon}>♟</span><span style={s.logoText}>Blitzly</span></div>
        <p style={s.tagline}>Choose your opponent's strength</p>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button
            style={{ ...s.diffOption, flex: 1, textAlign: 'center', justifyContent: 'center', background: color === 'white' ? colors.accentDim : colors.surfaceAlt, borderColor: color === 'white' ? colors.accent : colors.border }}
            onClick={() => setColor('white')}
          >
            Play as White
          </button>
          <button
            style={{ ...s.diffOption, flex: 1, textAlign: 'center', justifyContent: 'center', background: color === 'black' ? colors.accentDim : colors.surfaceAlt, borderColor: color === 'black' ? colors.accent : colors.border }}
            onClick={() => setColor('black')}
          >
            Play as Black
          </button>
        </div>
        {DIFFICULTIES.map(d => (
          <div key={d.elo} style={selected.elo === d.elo ? s.diffOptionActive : s.diffOption} onClick={() => setSelected(d)}>
            <span style={s.diffName}>{d.name}</span>
            <span style={s.diffElo}>ELO {d.elo}</span>
          </div>
        ))}
        <button style={{ ...s.btn, marginTop: '12px' }} onClick={() => onSelect(selected, color)}>Start game</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bot game
// ---------------------------------------------------------------------------

async function fetchBotMove(fen, elo) {
  const res = await fetch(`${API}/bot-move`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fen, elo }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.move;
}

function Game({ user, onLogout, difficulty, playerColor, onChangeDifficulty, onChangeColor }) {
  const gameRef = useRef(new Chess());
  const [fen, setFen] = useState(gameRef.current.fen());
  const [status, setStatus] = useState('Your move');
  const [moves, setMoves] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [moveFrom, setMoveFrom] = useState('');
  const [optionSquares, setOptionSquares] = useState({});
  const [pickingColor, setPickingColor] = useState(false);
  const sessionRef = useRef(0);
  const savedRef = useRef(false);

  useEffect(() => {
    if (savedRef.current) return;
    if (!status.startsWith('Checkmate') && !status.startsWith('Draw')) return;
    savedRef.current = true;

    let gameResult = '1/2-1/2';
    if (status.includes('You win')) gameResult = playerColor === 'white' ? '1-0' : '0-1';
    else if (status.includes('You lost')) gameResult = playerColor === 'white' ? '0-1' : '1-0';

    fetch(`${API}/save-game`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
      body: JSON.stringify({ pgn: gameRef.current.pgn(), result: gameResult, botLevel: difficulty?.elo, playerColor }),
    }).catch(err => console.error('Nie udalo sie zapisac partii:', err));
  }, [status]);

  useEffect(() => {
    if (playerColor === 'black') requestBotMove();
  }, []);

  const width = useWindowWidth();
  const isMobile = width < 600;
  const boardWidth = isMobile ? Math.min(width - 32, 360) : 420;

  const movePairs = [];
  for (let i = 0; i < moves.length; i += 2)
    movePairs.push({ n: Math.floor(i / 2) + 1, w: moves[i], b: moves[i + 1] || '' });

  const startNewGame = (color) => {
    sessionRef.current += 1;
    savedRef.current = false;
    gameRef.current = new Chess();
    setFen(gameRef.current.fen());
    setStatus('Your move');
    setMoves([]);
    setThinking(false);
    setMoveFrom('');
    setOptionSquares({});
    setPickingColor(false);
    onChangeColor(color);
    if (color === 'black') requestBotMove();
  };

  const requestBotMove = async () => {
    const session = sessionRef.current;
    setThinking(true);
    setStatus('Bot is thinking...');
    try {
      const g = gameRef.current;
      const uciMove = await fetchBotMove(g.fen(), difficulty.elo);
      if (session !== sessionRef.current) return;
      if (!uciMove || uciMove.length < 4) throw new Error('Invalid move from engine');
      const from = uciMove.slice(0, 2);
      const to = uciMove.slice(2, 4);
      const promotion = uciMove.length > 4 ? uciMove[4] : undefined;
      const botMove = g.move({ from, to, promotion });
      if (!botMove) throw new Error('Engine returned illegal move');
      setFen(g.fen());
      setMoves(prev => [...prev, botMove.san]);
      if (g.isCheckmate()) setStatus('Checkmate. You lost.');
      else if (g.isStalemate()) setStatus('Draw — stalemate');
      else if (g.isInsufficientMaterial()) setStatus('Draw — insufficient material');
      else if (g.isThreefoldRepetition()) setStatus('Draw — threefold repetition');
      else if (g.isDrawByFiftyMoves && g.isDrawByFiftyMoves()) setStatus('Draw — 50-move rule');
      else if (g.isDraw()) setStatus('Draw');
      else setStatus('Your move');
    } catch (err) {
      if (session === sessionRef.current) setStatus('Bot error — your move again');
    } finally {
      if (session === sessionRef.current) setThinking(false);
    }
  };

  const onDrop = (source, target) => {
    if (thinking) return false;
    const g = gameRef.current;
    if (g.isGameOver() || g.isThreefoldRepetition() || g.isDrawByFiftyMoves()) return false;
    const move = g.move({ from: source, to: target, promotion: 'q' });
    if (!move) return false;
    setFen(g.fen());
    setMoves(prev => [...prev, move.san]);
    if (g.isCheckmate()) { setStatus('Checkmate! You win!'); return true; }
    if (g.isStalemate()) { setStatus('Draw — stalemate'); return true; }
    if (g.isInsufficientMaterial()) { setStatus('Draw — insufficient material'); return true; }
    if (g.isThreefoldRepetition()) { setStatus('Draw — threefold repetition'); return true; }
    if (g.isDrawByFiftyMoves && g.isDrawByFiftyMoves()) { setStatus('Draw — 50-move rule'); return true; }
    if (g.isDraw()) { setStatus('Draw'); return true; }
    requestBotMove();
    return true;
  };

  const getMoveOptions = (square) => {
    const moves = gameRef.current.moves({ square, verbose: true });
    if (moves.length === 0) { setOptionSquares({}); return false; }
    const newSquares = {};
    moves.forEach((move) => {
      const isCapture = !!move.captured;
      newSquares[move.to] = {
        background: isCapture
          ? 'radial-gradient(circle, transparent 60%, ' + colors.accentDim + ' 60%)'
          : 'radial-gradient(circle, ' + colors.accent + '88 19%, transparent 20%)',
        borderRadius: '50%',
      };
    });
    newSquares[square] = { background: colors.accentDim };
    setOptionSquares(newSquares);
    return true;
  };

  const onSquareClick = (square) => {
    const g = gameRef.current;
    if (thinking || g.isGameOver()) return;
    if (!moveFrom) {
      const piece = g.get(square);
      if (piece && piece.color === g.turn()) {
        if (getMoveOptions(square)) setMoveFrom(square);
      }
      return;
    }
    if (moveFrom === square) { setMoveFrom(''); setOptionSquares({}); return; }
    const isLegalTarget = g.moves({ square: moveFrom, verbose: true }).some(m => m.to === square);
    if (!isLegalTarget) {
      const piece = g.get(square);
      setMoveFrom(''); setOptionSquares({});
      if (piece && piece.color === g.turn()) {
        if (getMoveOptions(square)) setMoveFrom(square);
      }
      return;
    }
    onDrop(moveFrom, square);
    setMoveFrom(''); setOptionSquares({});
  };

  return (
    <div style={{ background: colors.bg, minHeight: '100vh', fontFamily: "'Sora', sans-serif" }}>
      <div style={s.header}>
        <div style={s.headerLogo}><span style={{ fontSize: '20px' }}>♟</span><span style={s.headerLogoText}>Blitzly</span></div>
        <div style={s.headerRight}>
          <span style={s.eloTag}>ELO {user.elo}</span>
          <span style={{ fontSize: '14px', color: colors.textSub }}>{user.username}</span>
          <button style={s.btnTiny} onClick={onLogout}>Log out</button>
        </div>
      </div>
      <div style={isMobile ? s.gameLayoutMobile : s.gameLayout}>
        <div style={s.boardShell}>
          <Chessboard position={fen} onPieceDrop={onDrop} onSquareClick={onSquareClick} boardWidth={boardWidth}
            boardOrientation={playerColor}
            customSquareStyles={optionSquares}
            customDarkSquareStyle={{ backgroundColor: '#2d4a3e' }}
            customLightSquareStyle={{ backgroundColor: '#c8dfc8' }}
            customBoardStyle={{ borderRadius: '12px', boxShadow: `0 0 0 1px ${colors.border}` }} />
        </div>
        <div style={isMobile ? s.panelMobile : s.panel}>
          <div style={s.panelSection}>
            <p style={s.panelLabel}>Status</p>
            <p style={s.statusText}>{status}</p>
          </div>
          <div style={s.panelSection}>
            <p style={s.panelLabel}>Moves</p>
            {movePairs.length === 0
              ? <p style={s.hint}>No moves yet</p>
              : <div style={s.moveList}>{movePairs.slice(-8).map(m => (
                <div key={m.n}><span style={{ color: colors.textMuted, marginRight: '6px' }}>{m.n}.</span>{m.w}{m.b ? ` ${m.b}` : ''}</div>
              ))}</div>
            }
          </div>
          {pickingColor ? (
            <div style={s.colorPickRow}>
              <button style={{ ...s.colorPickBtn, ...s.colorPickBtnWhite }} onClick={() => startNewGame('white')} title="Play as White">♔</button>
              <button style={{ ...s.colorPickBtn, ...s.colorPickBtnBlack }} onClick={() => startNewGame('black')} title="Play as Black">♚</button>
            </div>
          ) : (
            <button style={s.resetBtn} onClick={() => setPickingColor(true)}>New game</button>
          )}
          <div style={{ ...s.panelSection, marginTop: '12px' }}>
            <p style={s.panelLabel}>Opponent</p>
            <p style={s.hint}>{difficulty.name} (ELO {difficulty.elo})<br />Powered by Stockfish.</p>
            <button style={{ ...s.resetBtn, marginTop: '8px' }} onClick={onChangeDifficulty}>Change difficulty</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Room: waiting screen
// ---------------------------------------------------------------------------

function RoomWaiting({ roomId, user, onLogout }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/room/${roomId}`;

  const copy = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ background: colors.bg, minHeight: '100vh', fontFamily: "'Sora', sans-serif" }}>
      <div style={s.header}>
        <div style={s.headerLogo}><span style={{ fontSize: '20px' }}>♟</span><span style={s.headerLogoText}>Blitzly</span></div>
        <div style={s.headerRight}>
          <span style={{ fontSize: '14px', color: colors.textSub }}>{user.username}</span>
          <button style={s.btnTiny} onClick={onLogout}>Log out</button>
        </div>
      </div>
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.logoRow}><span style={s.logoIcon}>♟</span><span style={s.logoText}>Waiting for opponent</span></div>
          <p style={{ ...s.tagline, marginBottom: '24px' }}>Share the link below to invite someone to play.</p>
          <div style={{ background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: colors.textSub, wordBreak: 'break-all', marginBottom: '14px' }}>
            {link}
          </div>
          <button style={s.btn} onClick={copy}>{copied ? 'Copied!' : 'Copy link'}</button>
          <p style={{ ...s.hint, marginTop: '20px', textAlign: 'center' }}>
            <span style={{ display: 'inline-block', animation: 'pulse 1.4s ease-in-out infinite' }}>●</span>
            {' '}Waiting...
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Random matchmaking: waiting screen (route /play)
// ---------------------------------------------------------------------------

function Matchmaking({ user, onLogout }) {
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState('');
  const socketRef = useRef(null);

  useEffect(() => {
    const socket = io(WS_URL, {
      auth: { token: user.token },
      transports: ['websocket'],
    });
    socketRef.current = socket;

    socket.on('connect_error', (err) => setErrorMsg(err.message || 'Connection failed'));
    socket.on('error', ({ message }) => setErrorMsg(message));
    socket.on('match-found', ({ roomId }) => navigate(`/room/${roomId}`, { replace: true }));

    socket.emit('find-match');

    return () => {
      socket.emit('cancel-match');
      socket.disconnect();
    };
  }, [user, navigate]);

  return (
    <div style={{ background: colors.bg, minHeight: '100vh', fontFamily: "'Sora', sans-serif" }}>
      <div style={s.header}>
        <div style={s.headerLogo}><span style={{ fontSize: '20px' }}>♟</span><span style={s.headerLogoText}>Blitzly</span></div>
        <div style={s.headerRight}>
          <span style={{ fontSize: '14px', color: colors.textSub }}>{user.username}</span>
          <button style={s.btnTiny} onClick={onLogout}>Log out</button>
        </div>
      </div>
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.logoRow}><span style={s.logoIcon}>♟</span><span style={s.logoText}>Finding opponent</span></div>
          {errorMsg && <p style={s.error}>{errorMsg}</p>}
          <p style={{ ...s.tagline, marginBottom: '24px' }}>10 min + 0 sec. First available opponent gets matched.</p>
          <p style={{ ...s.hint, textAlign: 'center', marginBottom: '20px' }}>
            <span style={{ display: 'inline-block', animation: 'pulse 1.4s ease-in-out infinite' }}>●</span>
            {' '}Searching...
          </p>
          <button style={s.btnGhost} onClick={() => navigate('/')}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Room: chat panel
// ---------------------------------------------------------------------------

function RoomChat({ messages, onSend }) {
  const [text, setText] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = () => {
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <p style={s.panelLabel}>Chat</p>
      <div style={{ flex: 1, overflowY: 'auto', maxHeight: '220px', marginBottom: '8px', fontSize: '13px', lineHeight: 1.6 }}>
        {messages.length === 0
          ? <p style={s.hint}>No messages yet</p>
          : messages.map((m, i) => (
            <div key={i} style={{ marginBottom: '4px' }}>
              <span style={{ color: colors.accent, fontWeight: 600 }}>{m.username}: </span>
              <span style={{ color: colors.text }}>{m.text}</span>
            </div>
          ))
        }
        <div ref={bottomRef} />
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          style={{ ...s.input, marginBottom: 0, flex: 1, padding: '8px 10px', fontSize: '13px' }}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(); }}
          placeholder="Message..."
        />
        <button style={{ ...s.btnTiny, whiteSpace: 'nowrap' }} onClick={send}>Send</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clock + captured pieces (chess.com-style, purely derived from chess.js history)
// ---------------------------------------------------------------------------

function formatClock(ms) {
  const totalSeconds = Math.max(0, Math.ceil((ms ?? 0) / 1000));
  const m = Math.floor(totalSeconds / 60);
  const sec = totalSeconds % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function Clock({ remainingMs, active }) {
  const low = remainingMs != null && remainingMs < 30000;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '6px',
      fontFamily: 'monospace', fontSize: '15px', fontWeight: 700,
      background: active ? colors.accentDim : colors.surfaceAlt,
      border: `1px solid ${active ? colors.accent : colors.border}`,
      color: low ? colors.danger : (active ? colors.accent : colors.text),
    }}>
      {formatClock(remainingMs)}
    </span>
  );
}

const PIECE_GLYPHS = {
  w: { q: '♕', r: '♖', b: '♗', n: '♘', p: '♙' },
  b: { q: '♛', r: '♜', b: '♝', n: '♞', p: '♟' },
};
const PIECE_ORDER = ['q', 'r', 'b', 'n', 'p'];

function CapturedPieces({ pieces, color }) {
  if (!pieces.length) return null;
  const sorted = [...pieces].sort((a, b) => PIECE_ORDER.indexOf(a) - PIECE_ORDER.indexOf(b));
  return (
    <span style={{ fontSize: '15px', letterSpacing: '-2px', color: colors.textSub }}>
      {sorted.map((p, i) => <span key={i}>{PIECE_GLYPHS[color][p]}</span>)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Room: game board (multiplayer)
// ---------------------------------------------------------------------------

function RoomGame({ user, roomId, myColor, initialFen, initialPgn, white, black, socket, onLogout,
  timeControl, initialWhiteRemainingMs, initialBlackRemainingMs, initialTurnStartedAt }) {
  const gameRef = useRef(new Chess());
  const [fen, setFen] = useState(initialFen);
  const [moves, setMoves] = useState([]);
  const [status, setStatus] = useState('');
  const [moveFrom, setMoveFrom] = useState('');
  const [optionSquares, setOptionSquares] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [players, setPlayers] = useState({ white, black });
  const [gameOver, setGameOver] = useState(null);
  const [promoMove, setPromoMove] = useState(null); // { from, to } for click-based promotion
  const [whiteRemainingMs, setWhiteRemainingMs] = useState(initialWhiteRemainingMs ?? null);
  const [blackRemainingMs, setBlackRemainingMs] = useState(initialBlackRemainingMs ?? null);
  const [turnStartedAt, setTurnStartedAt] = useState(initialTurnStartedAt ?? null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!timeControl || gameOver) return;
    const interval = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(interval);
  }, [timeControl, gameOver]);

  const width = useWindowWidth();
  const isMobile = width < 768;
  const boardWidth = isMobile ? Math.min(width - 32, 360) : 420;

  // Load existing game state
  useEffect(() => {
    if (initialPgn) {
      try { gameRef.current.loadPgn(initialPgn); } catch {
        try { gameRef.current.load(initialFen); } catch { /* keep start */ }
      }
    } else if (initialFen) {
      try { gameRef.current.load(initialFen); } catch { /* keep start */ }
    }
    setFen(gameRef.current.fen());
    const history = gameRef.current.history();
    setMoves(history);
    updateStatus();
  }, []);

  const updateStatus = useCallback(() => {
    const g = gameRef.current;
    const turn = g.turn() === 'w' ? 'white' : 'black';
    if (g.isCheckmate()) {
      const winner = turn === 'white' ? 'Black' : 'White';
      setStatus(`Checkmate — ${winner} wins`);
    } else if (g.isStalemate()) {
      setStatus('Draw — stalemate');
    } else if (g.isInsufficientMaterial()) {
      setStatus('Draw — insufficient material');
    } else if (g.isThreefoldRepetition()) {
      setStatus('Draw — threefold repetition');
    } else if (g.isDraw()) {
      setStatus('Draw');
    } else if (myColor === turn) {
      setStatus('Your move');
    } else {
      setStatus("Opponent's move");
    }
  }, [myColor]);

  useEffect(() => {
    if (!socket) return;

    socket.on('move', ({ from, to, promotion, san, fen: newFen, pgn: newPgn, whiteRemainingMs: w, blackRemainingMs: b, turnStartedAt: t }) => {
      gameRef.current.move({ from, to, promotion: promotion || 'q' });
      setFen(newFen);
      setMoves(gameRef.current.history());
      updateStatus();
      if (timeControl) {
        setWhiteRemainingMs(w);
        setBlackRemainingMs(b);
        setTurnStartedAt(t);
      }
    });

    socket.on('chat-message', (msg) => {
      setChatMessages(prev => [...prev, msg]);
    });

    socket.on('player-joined', ({ white: w, black: b, status: st }) => {
      setPlayers({ white: w, black: b });
      if (st === 'active') updateStatus();
    });

    socket.on('player-left', ({ username }) => {
      setChatMessages(prev => [...prev, { username: 'System', text: `${username} left the room`, ts: Date.now() }]);
    });

    socket.on('game-over', ({ result, fen: finalFen, pgn, whiteRemainingMs: w, blackRemainingMs: b }) => {
      setFen(finalFen);
      setGameOver(result);
      setStatus(resultToText(result, myColor, players));
      if (timeControl) {
        if (w != null) setWhiteRemainingMs(w);
        if (b != null) setBlackRemainingMs(b);
      }
    });

    socket.on('invalid-move', () => {
      updateStatus();
    });

    return () => {
      socket.off('move');
      socket.off('chat-message');
      socket.off('player-joined');
      socket.off('player-left');
      socket.off('game-over');
      socket.off('invalid-move');
    };
  }, [socket, updateStatus, myColor, players, timeControl]);

  const isMyTurn = () => {
    if (gameOver) return false;
    const turn = gameRef.current.turn() === 'w' ? 'white' : 'black';
    return myColor === turn;
  };

  const getMoveOptions = (square) => {
    const moves = gameRef.current.moves({ square, verbose: true });
    if (moves.length === 0) { setOptionSquares({}); return false; }
    const newSquares = {};
    moves.forEach((move) => {
      const isCapture = !!move.captured;
      newSquares[move.to] = {
        background: isCapture
          ? 'radial-gradient(circle, transparent 60%, ' + colors.accentDim + ' 60%)'
          : 'radial-gradient(circle, ' + colors.accent + '88 19%, transparent 20%)',
        borderRadius: '50%',
      };
    });
    newSquares[square] = { background: colors.accentDim };
    setOptionSquares(newSquares);
    return true;
  };

  const sendMove = (from, to, promotion) => {
    socket.emit('move', { roomId, from, to, promotion: promotion || undefined });
    setMoveFrom('');
    setOptionSquares({});
    setPromoMove(null);
  };

  const onPromotionPieceSelect = (piece, fromSquare, toSquare) => {
    if (!piece) { setPromoMove(null); return false; }
    const promotion = piece[1].toLowerCase(); // 'wQ' → 'q', 'bR' → 'r', etc.
    const from = fromSquare || promoMove?.from;
    const to = toSquare || promoMove?.to;
    if (!from || !to) return false;
    sendMove(from, to, promotion);
    return true;
  };

  const onDrop = (source, target) => {
    if (!isMyTurn()) return false;
    const g = gameRef.current;
    // Validate legality with queen as placeholder; actual promotion piece chosen via dialog
    const testMove = g.move({ from: source, to: target, promotion: 'q' });
    if (!testMove) return false;
    g.undo();
    // Promotion moves: return true so the library shows its dialog → onPromotionPieceSelect fires
    const isPromo = testMove.flags.includes('p');
    if (isPromo) return true;
    sendMove(source, target, undefined);
    return true;
  };

  const onSquareClick = (square) => {
    if (!isMyTurn()) return;
    const g = gameRef.current;
    if (!moveFrom) {
      const piece = g.get(square);
      if (piece && piece.color === g.turn()) {
        if (getMoveOptions(square)) setMoveFrom(square);
      }
      return;
    }
    if (moveFrom === square) { setMoveFrom(''); setOptionSquares({}); return; }
    const isLegalTarget = g.moves({ square: moveFrom, verbose: true }).some(m => m.to === square);
    if (!isLegalTarget) {
      const piece = g.get(square);
      setMoveFrom(''); setOptionSquares({});
      if (piece && piece.color === g.turn()) {
        if (getMoveOptions(square)) setMoveFrom(square);
      }
      return;
    }
    // Check if it's a promotion move (click-based: set state so Chessboard shows dialog)
    const movingPiece = g.get(moveFrom);
    const isPromo = movingPiece?.type === 'p' &&
      ((movingPiece.color === 'w' && square[1] === '8') || (movingPiece.color === 'b' && square[1] === '1'));
    if (isPromo) {
      setPromoMove({ from: moveFrom, to: square });
      setMoveFrom('');
      setOptionSquares({});
      return;
    }
    sendMove(moveFrom, square, undefined);
  };

  const movePairs = [];
  for (let i = 0; i < moves.length; i += 2)
    movePairs.push({ n: Math.floor(i / 2) + 1, w: moves[i], b: moves[i + 1] || '' });

  const opponent = myColor === 'white' ? players.black : players.white;
  const opponentName = opponent?.username || '—';

  const sideToMove = gameRef.current.turn() === 'w' ? 'white' : 'black';
  const displayWhiteMs = timeControl
    ? (sideToMove === 'white' && !gameOver ? Math.max(0, whiteRemainingMs - (now - turnStartedAt)) : whiteRemainingMs)
    : null;
  const displayBlackMs = timeControl
    ? (sideToMove === 'black' && !gameOver ? Math.max(0, blackRemainingMs - (now - turnStartedAt)) : blackRemainingMs)
    : null;

  const capturedByWhite = []; // black pieces captured by white
  const capturedByBlack = []; // white pieces captured by black
  gameRef.current.history({ verbose: true }).forEach((m) => {
    if (!m.captured) return;
    if (m.color === 'w') capturedByWhite.push(m.captured);
    else capturedByBlack.push(m.captured);
  });

  const topColor = myColor === 'black' ? 'white' : 'black';
  const bottomColor = topColor === 'white' ? 'black' : 'white';

  return (
    <div style={{ background: colors.bg, minHeight: '100vh', fontFamily: "'Sora', sans-serif" }}>
      <div style={s.header}>
        <div style={s.headerLogo}><span style={{ fontSize: '20px' }}>♟</span><span style={s.headerLogoText}>Blitzly</span></div>
        <div style={s.headerRight}>
          <span style={{ fontSize: '14px', color: colors.textSub }}>{user.username} ({myColor === 'observer' ? 'Observer' : myColor})</span>
          <button style={s.btnTiny} onClick={onLogout}>Log out</button>
        </div>
      </div>
      <div style={isMobile ? s.gameLayoutMobile : { ...s.gameLayout, flexWrap: 'nowrap', gap: '20px' }}>
        <div style={s.boardShell}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '13px', color: colors.textSub }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{myColor === 'white' ? `♚ ${opponentName}` : `♔ ${players.white?.username || '—'}`}</span>
              <CapturedPieces pieces={topColor === 'white' ? capturedByWhite : capturedByBlack} color={topColor === 'white' ? 'b' : 'w'} />
            </span>
            {timeControl && <Clock remainingMs={topColor === 'white' ? displayWhiteMs : displayBlackMs} active={sideToMove === topColor && !gameOver} />}
          </div>
          <Chessboard
            position={fen}
            onPieceDrop={myColor !== 'observer' ? onDrop : () => false}
            onSquareClick={myColor !== 'observer' ? onSquareClick : undefined}
            onPromotionPieceSelect={myColor !== 'observer' ? onPromotionPieceSelect : undefined}
            promotionToSquare={promoMove?.to}
            showPromotionDialog={!!promoMove}
            boardWidth={boardWidth}
            boardOrientation={myColor === 'observer' ? 'white' : myColor}
            customSquareStyles={optionSquares}
            customDarkSquareStyle={{ backgroundColor: '#2d4a3e' }}
            customLightSquareStyle={{ backgroundColor: '#c8dfc8' }}
            customBoardStyle={{ borderRadius: '12px', boxShadow: `0 0 0 1px ${colors.border}` }}
            arePiecesDraggable={myColor !== 'observer'}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', fontSize: '13px', color: colors.textSub }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{myColor === 'white' ? `♔ ${user.username}` : `♚ ${players.black?.username || '—'}`}</span>
              <CapturedPieces pieces={bottomColor === 'white' ? capturedByWhite : capturedByBlack} color={bottomColor === 'white' ? 'b' : 'w'} />
            </span>
            {timeControl && <Clock remainingMs={bottomColor === 'white' ? displayWhiteMs : displayBlackMs} active={sideToMove === bottomColor && !gameOver} />}
          </div>
        </div>
        <div style={isMobile ? s.panelMobile : { ...s.panel, width: '260px' }}>
          <div style={s.panelSection}>
            <p style={s.panelLabel}>Status</p>
            <p style={s.statusText}>{status || "Waiting for game..."}</p>
          </div>
          <div style={s.panelSection}>
            <p style={s.panelLabel}>Moves</p>
            {movePairs.length === 0
              ? <p style={s.hint}>No moves yet</p>
              : <div style={s.moveList}>{movePairs.slice(-8).map(m => (
                <div key={m.n}><span style={{ color: colors.textMuted, marginRight: '6px' }}>{m.n}.</span>{m.w}{m.b ? ` ${m.b}` : ''}</div>
              ))}</div>
            }
          </div>
          <div style={{ ...s.panelSection, flexGrow: 1 }}>
            <RoomChat messages={chatMessages} onSend={(text) => socket.emit('chat-message', { roomId, text })} />
          </div>
        </div>
      </div>
    </div>
  );
}

function resultToText(result, myColor) {
  if (result === '1/2-1/2') return 'Draw';
  if ((result === '1-0' && myColor === 'white') || (result === '0-1' && myColor === 'black')) return 'You win!';
  return 'You lost.';
}

// ---------------------------------------------------------------------------
// Room page (route /room/:id)
// ---------------------------------------------------------------------------

function RoomPage({ user, onLogin, onLogout }) {
  const { id: roomId } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('connecting'); // connecting | waiting | playing | error
  const [myColor, setMyColor] = useState(null);
  const [roomState, setRoomState] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    const socket = io(WS_URL, {
      auth: { token: user.token },
      transports: ['websocket'],
    });
    socketRef.current = socket;

    socket.on('connect_error', (err) => {
      setPhase('error');
      setErrorMsg(err.message || 'Connection failed');
    });

    socket.on('room-joined', ({ color, fen, pgn, status, white, black, timeControl, whiteRemainingMs, blackRemainingMs, turnStartedAt }) => {
      setMyColor(color);
      setRoomState({ fen, pgn, white, black, status, timeControl, whiteRemainingMs, blackRemainingMs, turnStartedAt });
      if (color !== 'observer' && (status === 'waiting' || (status === 'active' && !black))) {
        setPhase('waiting');
      } else {
        setPhase('playing');
      }
      if (status === 'active' && white && black) setPhase('playing');
    });

    socket.on('player-joined', ({ status }) => {
      if (status === 'active') setPhase('playing');
    });

    socket.on('error', ({ message }) => {
      setPhase('error');
      setErrorMsg(message);
    });

    socket.emit('join-room', roomId);

    return () => socket.disconnect();
  }, [user, roomId]);

  if (!user) {
    return <Login onLogin={onLogin} redirectTo={`/room/${roomId}`} />;
  }

  if (phase === 'connecting') {
    return (
      <div style={{ ...s.page, color: colors.textSub, fontSize: '14px' }}>Connecting...</div>
    );
  }

  if (phase === 'error') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <p style={s.error}>{errorMsg}</p>
          <button style={s.btn} onClick={() => navigate('/')}>Back to home</button>
        </div>
      </div>
    );
  }

  if (phase === 'waiting') {
    return <RoomWaiting roomId={roomId} user={user} onLogout={onLogout} />;
  }

  return (
    <RoomGame
      user={user}
      roomId={roomId}
      myColor={myColor}
      initialFen={roomState?.fen}
      initialPgn={roomState?.pgn}
      white={roomState?.white}
      black={roomState?.black}
      socket={socketRef.current}
      onLogout={onLogout}
      timeControl={roomState?.timeControl}
      initialWhiteRemainingMs={roomState?.whiteRemainingMs}
      initialBlackRemainingMs={roomState?.blackRemainingMs}
      initialTurnStartedAt={roomState?.turnStartedAt}
    />
  );
}

// ---------------------------------------------------------------------------
// Play page (route /play) — requires login, hands off to Matchmaking
// ---------------------------------------------------------------------------

function PlayPage({ user, onLogin, onLogout }) {
  if (!user) return <Login onLogin={onLogin} redirectTo="/play" />;
  return <Matchmaking user={user} onLogout={onLogout} />;
}

// ---------------------------------------------------------------------------
// Home page (existing singleplayer flow)
// ---------------------------------------------------------------------------

function HomePage({ user, onLogin, onLogout }) {
  const [showSplash, setShowSplash] = useState(!user);
  const [mode, setMode] = useState(null); // null | 'bot'
  const [difficulty, setDifficulty] = useState(null);
  const [playerColor, setPlayerColor] = useState('white');

  if (showSplash && !user) return (
    <>
      <style>{'@keyframes splashIn { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }'}</style>
      <Splash onDone={() => setShowSplash(false)} />
    </>
  );
  if (!user) return <Login onLogin={onLogin} />;
  if (!mode) return <ModePicker user={user} onSelectBot={() => setMode('bot')} onLogout={onLogout} />;
  if (!difficulty) return <DifficultyPicker onSelect={(d, c) => { setDifficulty(d); setPlayerColor(c); }} />;
  return (
    <Game
      user={user}
      onLogout={() => { onLogout(); setMode(null); setDifficulty(null); }}
      difficulty={difficulty}
      playerColor={playerColor}
      onChangeDifficulty={() => setDifficulty(null)}
      onChangeColor={setPlayerColor}
    />
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('blitzly_user')); } catch { return null; }
  });

  const onLogin = (data) => {
    setUser(data);
    try { localStorage.setItem('blitzly_user', JSON.stringify(data)); } catch {}
  };

  const onLogout = () => {
    setUser(null);
    try { localStorage.removeItem('blitzly_user'); } catch {}
  };

  return (
    <BrowserRouter>
      <style>
        {`
          @keyframes splashIn { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
          @keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
        `}
      </style>
      <Routes>
        <Route path="/room/:id" element={<RoomPage user={user} onLogin={onLogin} onLogout={onLogout} />} />
        <Route path="/play" element={<PlayPage user={user} onLogin={onLogin} onLogout={onLogout} />} />
        <Route path="/*" element={<HomePage user={user} onLogin={onLogin} onLogout={onLogout} />} />
      </Routes>
    </BrowserRouter>
  );
}
